import * as React from 'react';
import cn from 'classnames';

import MediaQuery from 'react-responsive';
import { TABLET_MIDDLE_WIDTH } from '../../../utils';
import { GdsSeatService, ServicesMap } from '../../types';
import { Money } from '../../../types';
import { useTheme } from '../../../theme';
import { FlightClass, FlightStaticInfoFragment } from '@websky/graphql';
import SeatInfoGroup from './SeatInfoGroup/SeatInfoGroup';
import FreeSeatInfo from './FreeSeatInfo/FreeSeatInfo';
import { useRenderers } from '../../../renderProps';
import { SeatsInfoProps } from './types';
import { useTranslation } from 'react-i18next';
import ChangeSeat from './ChangeSeat/ChangeSeat';

export interface Props extends SeatsInfoProps {
	seatsInfo: GdsSeatService[];
	showPrice?: boolean;
	amenities?: FlightStaticInfoFragment['amenities'];
	services?: ServicesMap;
	hasFreeSeats?: boolean;
	hasUnavailableSeats?: boolean;
	minSeatChangeChargePrice?: Money | null;
}

const SeatsInfo: React.FunctionComponent<Props> = props => {
	const { t } = useTranslation('SeatMap');
	const theme = useTheme('SeatMap').SeatsInfoStyles;

	const { SeatInfoGroup: SeatInfoGroupOverride = SeatInfoGroup } = useRenderers();

	const seatsByClassesMap = React.useMemo(() => {
		const seatsByClasses = new Map<FlightClass, GdsSeatService[]>();

		props.seatsInfo.forEach(seat => {
			seatsByClasses.set(
				seat.serviceClass,
				seatsByClasses.has(seat.serviceClass) ? [...seatsByClasses.get(seat.serviceClass), seat] : [seat]
			);
		});

		return seatsByClasses;
	}, [props.seatsInfo]);

	const seatsByClasses = React.useMemo(() => {
		const seatsByClasses: {
			image: string;
			classType: FlightClass;
			seats: GdsSeatService[];
		}[] = [];

		seatsByClassesMap.forEach((seats, classType) => {
			seatsByClasses.push({
				classType,
				seats,
				image: seats.find(seat => !!seat.serviceImage)?.serviceImage
			});
		});

		return seatsByClasses;
	}, [seatsByClassesMap]);

	const renderSeatsByClasses = (mobile?: boolean) => {
		const seatsInfoGroups = seatsByClasses.map(seats => {
			return (
				<div key={seats.classType}>
					<SeatInfoGroupOverride
						seatService={seats.seats}
						fullInfo={mobile}
						hideImage={false}
						segmentId={props.segmentId}
					/>
				</div>
			);
		});

		if (props.hasFreeSeats) {
			seatsInfoGroups.push(
				<FreeSeatInfo key={seatsInfoGroups.length} className={theme.seatInfo__freeSeat_container} />
			);
		}

		if (props.minSeatChangeChargePrice) {
			seatsInfoGroups.push(
				<ChangeSeat
					key={seatsInfoGroups.length}
					className={theme.seatInfo__freeSeat_container}
					price={props.minSeatChangeChargePrice}
				/>
			);
		}

		if (props.hasUnavailableSeats) {
			seatsInfoGroups.push(
				<FreeSeatInfo
					key={seatsInfoGroups.length}
					className={cn(theme.seatInfo__freeSeat_container, theme.seatInfo__unavailableSeat_container)}
					seatClassName={theme.freeSeat__seat_unavailable}
					customTitle={t('Unavailable seats')}
				/>
			);
		}

		return seatsInfoGroups;
	};

	return (
		<>
			<MediaQuery minWidth={+TABLET_MIDDLE_WIDTH + 1}>
				<aside className={cn(theme.info)}>{renderSeatsByClasses()}</aside>
			</MediaQuery>
			<MediaQuery maxWidth={TABLET_MIDDLE_WIDTH}>{renderSeatsByClasses(true)}</MediaQuery>
		</>
	);
};

export default SeatsInfo;
